import { useContext, useEffect, useState } from "react";
import { LocationBookmarksV1Service } from "../../../services/api/api";
import { TbMapPinPlus } from "react-icons/tb";
import { IoBookmarkOutline } from "react-icons/io5";
import { ObjectForm } from "../objectForm";
import { Math as CesiumMath } from "cesium";
import { Themes } from "../../../enums/themes";
import Context from "../../utils/context";
import { BookmarksList } from "./bookmarksList";

export interface ILocationBookmark {
  id: string;
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  altitude: number;
  created?: Date;
  updated?: Date;
}

export interface ICreateLocationBookmark {
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  altitude: number;
}

interface LocationBookmarkPanelProps {
  viewer?: any;
}

export const LocationBookmarkPanel = (props: LocationBookmarkPanelProps) => {
  const context = useContext(Context);

  const [allLocationBookmarks, setAllLocationBookmarks] = useState<
    ILocationBookmark[] | null
  >([]);
  const [showSavingForm, setShowSavingForm] = useState(false);
  const [showBookmarksList, setShowBookmarksList] = useState(false);
  const [formTitle, setFormTitle] = useState("Saving Location");
  const [currentBookmark, setCurrentBookmark] =
    useState<ILocationBookmark | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getLocationBookmarks();
  }, []);

  const getLocationBookmarks = async () => {
    const LocationBookmarksService = new LocationBookmarksV1Service();
    const response = await LocationBookmarksService.getMyLocationBookmarks();
    setAllLocationBookmarks(response.data);
  };

  const createLocationBookmark = async (name: string, description?: string) => {
    if (props.viewer) {
      const viewer = props.viewer;
      const cartographic = viewer.camera.positionCartographic;
      if (!cartographic) {
        console.error("Camera position is not available.");
        return;
      }
      const longitude = CesiumMath.toDegrees(cartographic.longitude);
      const latitude = CesiumMath.toDegrees(cartographic.latitude);
      const altitude = cartographic.height;

      const bookmark: ICreateLocationBookmark = {
        name,
        description: description || "",
        latitude,
        longitude,
        altitude,
      };
      try {
        setIsSaving(true);
        const LocationBookmarksService = new LocationBookmarksV1Service();
        await LocationBookmarksService.create(bookmark);
        getLocationBookmarks();

        context.setAlert({
          message: "Location bookmark created successfully.",
          theme: Themes.Success,
        });
      } catch (error) {
        console.error("Error creating location bookmark:", error);
        context.setAlert({
          message: "Error creating location bookmark. Please try again.",
          theme: Themes.Danger,
        });
      } finally {
        setShowSavingForm(false);
        setIsSaving(false);
      }
    }
  };

  const updateLocationBookmark = async (bookmark: ILocationBookmark) => {
    setFormTitle("Edit Location");
    setShowSavingForm(true);
    setCurrentBookmark(bookmark);
  };

  const editLocationBookmark = async (name: string, description?: string) => {
    if (!currentBookmark) return;
    const bookmark: ILocationBookmark = {
      ...currentBookmark,
      name,
      description,
    };

    try {
      const LocationBookmarksService = new LocationBookmarksV1Service();
      await LocationBookmarksService.update(currentBookmark.id, bookmark);
      getLocationBookmarks();

      context.setAlert({
        message: "Location bookmark updated successfully.",
        theme: Themes.Success,
      });
    } catch (error) {
      console.error("Error updating location bookmark:", error);
      context.setAlert({
        message: "Error updating location bookmark. Please try again.",
        theme: Themes.Danger,
      });
    } finally {
      setShowSavingForm(false);
      setCurrentBookmark(null);
      setIsSaving(false);
    }
  };

  const deleteLocationBookmark = async (id: string) => {
    try {
      const LocationBookmarksService = new LocationBookmarksV1Service();
      await LocationBookmarksService.delete(id);
      context.setAlert({
        message: "Location bookmark deleted successfully.",
        theme: Themes.Success,
      });

      getLocationBookmarks();
    } catch (error) {
      console.error("Error deleting location bookmark:", error);
      context.setAlert({
        message: "Error deleting location bookmark. Please try again.",
        theme: Themes.Danger,
      });
    }
  };

  const handleSaveLocation = () => {
    setFormTitle("Save New Location");
    setShowSavingForm(true);
  };

  const handleCancel = () => {
    setShowSavingForm(false);
  };

  const handleShowSavedLocations = () => {
    setShowBookmarksList(true);
  };

  return (
    <div className="fixed top-20 right-[20%] flex gap-4" style={{ zIndex: 5 }}>
      <button
        className="flex items-center gap-2 px-4 py-2 bg-gray-500/80 backdrop-blur-md text-white rounded-full shadow-md hover:bg-gray-700/90 transition"
        onClick={handleSaveLocation}
      >
        <TbMapPinPlus size={18} />
        <span>Save Location</span>
      </button>
      <button
        className="flex items-center gap-2 px-4 py-2 bg-gray-500/80 backdrop-blur-md text-white rounded-full shadow-md hover:bg-gray-700/90 transition"
        onClick={handleShowSavedLocations}
      >
        <IoBookmarkOutline size={18} />
        <span>Saved Locations</span>
      </button>
      {currentBookmark != null ? (
        <ObjectForm
          title={formTitle}
          onSave={editLocationBookmark}
          onCancel={handleCancel}
          showDescription={true}
          show={showSavingForm}
          isSaving={isSaving}
          name={currentBookmark?.name || ""}
          description={currentBookmark?.description || ""}
        />
      ) : (
        <ObjectForm
          title={formTitle}
          onSave={createLocationBookmark}
          onCancel={handleCancel}
          showDescription={true}
          show={showSavingForm}
          isSaving={isSaving}
        />
      )}

      <BookmarksList
        bookmarks={allLocationBookmarks || []}
        show={showBookmarksList}
        onClose={() => setShowBookmarksList(false)}
        removeLocationBookmark={deleteLocationBookmark}
        updateLocationBookmark={updateLocationBookmark}
      />
    </div>
  );
};
